import { formatDateTime } from '@customerio/pluma-components/react/utils/formatted-date-time';
import type { BillingCoupon, BillingSubscription } from './index';
import { BillingSystem, type BillingPlan } from './types';
import { OrdwayChargeType } from './ordway/utils';
import { PlanType } from './plans';
import { formatCentsToUsd } from '@/utils/currency';
import { isEmptyString } from '@/utils/string';
import { BILLING_TIME_ZONE } from './time-zone';

function couponCode(coupon: BillingCoupon): string | undefined {
  return 'code' in coupon ? coupon.code : undefined;
}

/** Whether a coupon discount applies to the current subscription. */
export function hasCouponDiscount(
  coupon: BillingCoupon | undefined,
  subscription: BillingSubscription | undefined,
  isFutureSubscription: boolean,
): boolean {
  if (isFutureSubscription || coupon == null) {
    return false;
  }

  if (subscription?.billingSystem === BillingSystem.Ordway) {
    if (subscription.coupon_code == null || subscription.coupon_code !== couponCode(coupon)) {
      return false;
    }
  }

  const percentOff = coupon.percent_off ?? 0;
  const amountOff = coupon.amount_off ?? 0;
  return percentOff > 0 || amountOff > 0;
}

/**
 * The plan price before any coupon discount, in cents. Ordway's `basePrice` is already discounted, so
 * the pre-coupon price is summed from the recurring products' `list_price`.
 */
export function priceBeforeDiscount(plan: BillingPlan, subscription: BillingSubscription | undefined): number {
  if (subscription?.billingSystem === BillingSystem.Ordway) {
    if (subscription.planType === PlanType.Startup) {
      return 0;
    }
    const recurringProducts = (subscription.plans ?? []).filter(
      (product) => product.charge_type !== OrdwayChargeType.Usage,
    );
    return recurringProducts.reduce((acc, product) => Math.round(Number.parseFloat(product.list_price) * 100) + acc, 0);
  }

  return plan.basePrice;
}

/**
 * Whether a coupon still applies at the instant of the next charge, for a rail whose end date and
 * charge date are exact instants (Stripe, Metronome).
 *
 * The comparison is strict: the renewal invoice is created at the charge instant and falls outside the
 * discount window, so a coupon ending at exactly that instant does not discount it. A non-positive end
 * date is the unset value the Stripe rail serializes, so it means no expiry; with no known charge date
 * the estimate keeps the coupon rather than guessing.
 */
export function couponActiveForCharge(couponEndsAt: number | undefined, chargeDate: number | undefined): boolean {
  if (couponEndsAt == null || couponEndsAt <= 0 || chargeDate == null) {
    return true;
  }
  return couponEndsAt > chargeDate;
}

function utcCalendarDay(date: string): number | null {
  const parsed = new Date(date);
  const time = parsed.getTime();
  if (Number.isNaN(time)) {
    return null;
  }
  return Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
}

/**
 * Whether a coupon still applies on the charge, for a rail whose dates are calendar days (Ordway).
 *
 * Both are date-only strings, so they are compared as UTC calendar days, in one frame. Anchoring the
 * end date to the viewer's local day instead would drift it past UTC midnight west of UTC, and keep a
 * coupon that ends the day before the charge. An empty or unparsable date means no end or no known
 * charge, so the coupon stays.
 */
export function couponActiveForChargeDate(
  expiration: string | null | undefined,
  chargeDate: string | null | undefined,
): boolean {
  if (expiration == null || chargeDate == null || isEmptyString(expiration) || isEmptyString(chargeDate)) {
    return true;
  }
  const expirationDay = utcCalendarDay(expiration);
  const chargeDay = utcCalendarDay(chargeDate);
  if (expirationDay == null || chargeDay == null) {
    return true;
  }
  return expirationDay >= chargeDay;
}

/**
 * Whether a coupon reduces the overage lines, not just the base price.
 *
 * A dollar-amount coupon is a credit against the base price, so it never touches overages. A
 * percentage coupon does, but only on the rail that prices the whole bill as one (Stripe). Ordway
 * meters overages as a separate product its coupons do not cover, and it is the rail that reports the
 * overages apart from the rest. Rare either way — most contracts discount the base alone.
 */
export function couponDiscountsOverages(coupon: BillingCoupon, reportsWholeBill: boolean): boolean {
  if (!reportsWholeBill) {
    return false;
  }
  const amountOff = coupon.amount_off ?? 0;
  const percentOff = coupon.percent_off ?? 0;
  return amountOff <= 0 && percentOff > 0;
}

/** The coupon discount, in whole cents, clamped between 0 and the pre-discount price. */
export function couponDiscountCents(beforeCents: number, coupon: BillingCoupon | undefined): number {
  if (coupon == null) {
    return 0;
  }

  const amountOff = coupon.amount_off ?? 0;
  let discount: number;
  if (amountOff > 0) {
    discount = amountOff;
  } else {
    discount = beforeCents * ((coupon.percent_off ?? 0) / 100);
  }

  return Math.round(Math.min(Math.max(0, discount), beforeCents));
}

/** The coupon discount label (e.g. "$10 off until 1/5/26"), or null. */
export function couponDiscountLabel(
  coupon: BillingCoupon | undefined,
  couponEndsAt: number | undefined,
): string | null {
  if (coupon == null) {
    return null;
  }

  const amountOff = coupon.amount_off ?? 0;
  const percentOff = coupon.percent_off ?? 0;
  let discountText: string;
  if (amountOff > 0) {
    discountText = `${formatCentsToUsd(amountOff)} off`;
  } else if (percentOff > 0) {
    discountText = `${percentOff}% off`;
  } else {
    return null;
  }

  if (couponEndsAt != null) {
    discountText = `${discountText} until ${formatDateTime(couponEndsAt * 1000, 'medium', { day: '2-digit', timeZone: BILLING_TIME_ZONE })}`;
  }

  return discountText;
}
